import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  sessionRecordApi,
  SessionRecord,
  SessionRecordRowInput,
} from "./api";
import { studentsApi, Student } from "../students/api";
import { useMasterData } from "../../../hooks/useMasterData";

interface AcademicSessionOption {
  id: string;
  name: string;
}

interface SectionOption {
  id: string;
  name: string;
  classId: string;
}

const SessionRecordEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const studentId = id as string;
  const navigate = useNavigate();

  const { masters, fetchMasters, fetchSections } = useMasterData();

  const [student, setStudent] = useState<Student | null>(null);
  const [rows, setRows] = useState<SessionRecordRowInput[]>([]);
  const [sessions, setSessions] = useState<AcademicSessionOption[]>([]);
  const [sectionOptionsByClass, setSectionOptionsByClass] = useState<
    Record<string, SectionOption[]>
  >({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const loadInitialData = async () => {
      if (!studentId) {
        navigate("/scholar-register/session-record");
        return;
      }
      setLoading(true);
      try {
        await fetchMasters();

        const [studentRes, sessionsRes] = await Promise.all([
          studentsApi.getById(studentId),
          axios.get<any[]>("/system-settings/academic-sessions"),
        ]);

        setStudent(studentRes);

        const sessionItems = sessionsRes.data || [];
        setSessions(
          sessionItems.map((s: any) => ({
            id: s.id,
            name: s.name,
          })),
        );

        let history: SessionRecord[] = [];
        try {
          history = await sessionRecordApi.historyByStudent(studentId);
        } catch {
          history = [];
        }

        let mappedRows: SessionRecordRowInput[] = [];

        if (history.length > 0) {
          mappedRows = history.map((rec: SessionRecord) => ({
            id: rec.id,
            academicSessionId: rec.academicSessionId,
            classId: rec.classId,
            sectionId: rec.sectionId,
            formNumber: rec.formNumber || "",
            dateOfAdmission: rec.dateOfAdmission
              ? rec.dateOfAdmission.substring(0, 10)
              : studentRes.admissionDate
              ? String(studentRes.admissionDate).substring(0, 10)
              : "",
          }));
        } else if (
          (studentRes as any).sessionRecords &&
          (studentRes as any).sessionRecords.length > 0
        ) {
          mappedRows = (studentRes as any).sessionRecords.map((rec: any) => ({
            id: rec.id,
            academicSessionId: rec.academicSessionId,
            classId: rec.classId,
            sectionId: rec.sectionId,
            formNumber: "",
            dateOfAdmission: rec.dateOfAdmission
              ? String(rec.dateOfAdmission).substring(0, 10)
              : studentRes.admissionDate
              ? String(studentRes.admissionDate).substring(0, 10)
              : "",
          }));
        } else {
          const activeSession = sessionItems.find((s: any) => s.isActive);
          if (
            activeSession &&
            studentRes.classId &&
            studentRes.sectionId
          ) {
            mappedRows = [
              {
                academicSessionId: activeSession.id,
                classId: studentRes.classId,
                sectionId: studentRes.sectionId,
                formNumber: "",
                dateOfAdmission: studentRes.admissionDate
                  ? String(studentRes.admissionDate).substring(0, 10)
                  : "",
              },
            ];
          }
        }

        setRows(mappedRows);

        const classIds = Array.from(
          new Set(mappedRows.map((r) => r.classId).filter(Boolean)),
        );
        await Promise.all(
          classIds.map(async (classId) => {
            const secs = await fetchSections(classId);
            setSectionOptionsByClass((prev) => ({
              ...prev,
              [classId]: secs.map((s) => ({
                id: s.id,
                name: s.name,
                classId,
              })),
            }));
          }),
        );
      } catch {
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [studentId, fetchMasters, fetchSections, navigate]);

  const handleRowChange = (
    index: number,
    field: keyof SessionRecordRowInput,
    value: string,
  ) => {
    setRows((prev) => {
      const updated = [...prev];
      const row = { ...updated[index], [field]: value };

      if (field === "classId") {
        row.sectionId = "";
        if (value) {
          loadSectionsForClass(value);
        }
      }

      updated[index] = row;
      return updated;
    });
  };

  const loadSectionsForClass = async (classId: string) => {
    if (!classId || sectionOptionsByClass[classId]) return;
    try {
      const secs = await fetchSections(classId);
      setSectionOptionsByClass((prev) => ({
        ...prev,
        [classId]: secs.map((s) => ({
          id: s.id,
          name: s.name,
          classId,
        })),
      }));
    } catch {
    }
  };

  const handleAddRow = () => {
    setError(null);
    setSuccess(null);

    setRows((prev) => [
      ...prev,
      {
        academicSessionId: "",
        classId: "",
        sectionId: "",
        formNumber: "",
        dateOfAdmission: "",
      },
    ]);
  };

  const handleRemoveRow = (index: number) => {
    setError(null);
    setSuccess(null);
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!studentId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const incompleteIndex = rows.findIndex((r) => {
        const hasAny =
          r.academicSessionId || r.classId || r.sectionId || r.dateOfAdmission;
        const hasAll = r.academicSessionId && r.classId && r.sectionId;
        return hasAny && !hasAll;
      });
      if (incompleteIndex !== -1) {
        setError(
          `Please select Session, Class and Section for row #${
            incompleteIndex + 1
          } before saving.`,
        );
        return;
      }

      const payload = rows.filter(
        (r) => r.academicSessionId && r.classId && r.sectionId,
      );
      if (payload.length === 0) {
        setError(
          "Please add at least one valid session record before saving.",
        );
        return;
      }

      const refreshed = await sessionRecordApi.replaceForStudent(
        studentId,
        payload,
      );

      const refreshedStudent = await studentsApi.getById(studentId);
      setStudent(refreshedStudent);

      const mappedRows: SessionRecordRowInput[] = refreshed.map(
        (rec: SessionRecord) => ({
          id: rec.id,
          academicSessionId: rec.academicSessionId,
          classId: rec.classId,
          sectionId: rec.sectionId,
          formNumber: rec.formNumber || "",
          dateOfAdmission: rec.dateOfAdmission
            ? rec.dateOfAdmission.substring(0, 10)
            : refreshedStudent.admissionDate
            ? String(refreshedStudent.admissionDate).substring(0, 10)
            : "",
        }),
      );
      setRows(mappedRows);

      setSuccess("Session records updated successfully");
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      const finalMessage = Array.isArray(msg)
        ? msg.join(", ")
        : typeof msg === "string"
        ? msg
        : "Failed to save session records.";
      setError(finalMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Manage Session Records
        </Typography>
        {student && (
          <Paper
            sx={{
              mt: 2,
              mb: 2,
              p: 3,
              borderRadius: 2,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Admission No
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {student.admissionNumber || "-"}
                </Typography>

                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Scholar No
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {student.scholarNumber || "-"}
                  </Typography>
                </Box>

                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Student Name
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {student.firstName} {student.lastName}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Class
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {student.class?.name || "-"}
                </Typography>

                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Section
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {student.section?.name || "-"}
                  </Typography>
                </Box>

                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Date of Birth
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {student.dateOfBirth
                      ? new Date(student.dateOfBirth).toLocaleDateString("en-GB")
                      : "-"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}
      </Box>

      <Paper sx={{ p: 2 }}>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="subtitle1">Session Records</Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddRow}
          >
            Add Session
          </Button>
        </Box>

        <TableContainer sx={{ maxHeight: 420 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Session</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Section</TableCell>
                <TableCell>Scholar No</TableCell>
                <TableCell>Date of Admission</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const classSections =
                  (row.classId && sectionOptionsByClass[row.classId]) || [];
                return (
                  <TableRow
                    key={row.id || index}
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: "grey.50",
                      },
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <TableCell sx={{ width: 60 }}>{index + 1}</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={row.academicSessionId}
                        onChange={(e) =>
                          handleRowChange(
                            index,
                            "academicSessionId",
                            e.target.value,
                          )
                        }
                      >
                        {sessions.map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={row.classId}
                        onChange={(e) =>
                          handleRowChange(index, "classId", e.target.value)
                        }
                      >
                        {(masters.classes || []).map((cls: any) => (
                          <MenuItem key={cls.id} value={cls.id}>
                            {cls.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={row.sectionId}
                        onChange={(e) =>
                          handleRowChange(index, "sectionId", e.target.value)
                        }
                        disabled={!row.classId}
                      >
                        {classSections.map((sec) => (
                          <MenuItem key={sec.id} value={sec.id}>
                            {sec.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <Typography variant="body2">
                        {student?.scholarNumber || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <TextField
                        type="date"
                        fullWidth
                        size="small"
                        value={row.dateOfAdmission || ""}
                        onChange={(e) =>
                          handleRowChange(
                            index,
                            "dateOfAdmission",
                            e.target.value,
                          )
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveRow(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No academic session records found for this student.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            disabled={saving}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            Save
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default SessionRecordEdit;
